import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { from, Observable, throwError, EMPTY } from 'rxjs';
import { switchMap, catchError, delay } from 'rxjs/operators';

import { environment } from '@environments/environment';
import HelpersUtils from '@constants/helpers';
import Resources from '@constants/resourses';

import { JwtService } from './jwt.service';
import { UtilityService } from './utility.service';
import { SessionService } from './session.service';
import { IErrorHandling } from '../models/utility.interface';

const SESSION_TOKEN_ENDPOINTS = ['/v2/users/user-details', '/v2/users/saved-details', '/v2/users/user-address'];

@Injectable({
  providedIn: 'root'
})
export class JwtInterseptorService {
  // Shared across concurrent requests (this service is a root singleton) so that if several
  // requests 401 at the same time, only one popup is shown and all of them wait on it.
  private sessionExpiredDialogRef: MatDialogRef<any> | null = null;

  constructor(private jwtapi: JwtService, private utilityService: UtilityService, private sessionService: SessionService) { }

  private isSessionTokenEndpoint(req: HttpRequest<any>): boolean {
    return req.method === 'POST' && [...SESSION_TOKEN_ENDPOINTS, '/v1/users/sessionData'].some(endpoint => req.url.includes(endpoint));
  }

  private isSessionExpiredError(err: any): boolean {
    return err?.status === 401 || err?.error?.statusCode === 401;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let isExpired = true;

    if (req.url.includes('/oauth/authorize') || req.url?.split(environment.S3_url)?.length === 2 || req.url?.split('payment_methods')[0] === "https://api.stripe.com/v1/" || req.url?.split('customers')[0] === "https://api.stripe.com/v1/" || req.url?.split('setup_intents')[0] === "https://api.stripe.com/v1/" || req.url === "https://api.ipify.org?format=json" || req.url.includes("https://api.perse.io")) {
      if (req.url?.split(environment.S3_url)?.length === 2) {
        // to prevent caching
        const clone = req.clone({ setHeaders: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } });
        return next.handle(clone);
      } else {
        return next.handle(req);
      }
    } else {
      // Generate DPoP token asynchronously
      return from(HelpersUtils.generateDPoPToken('basic', req.url, req.method)).pipe(
        switchMap((res: any) => {
          // Pass both proof JWT (identifer) and hash for cryptographic binding
          return this.jwtapi.addKeyToCache(res.cache, res.identifer).pipe(delay(600)).pipe(switchMap((response) => {
            if (!!res.identifer) {
              const setHeaders: { [name: string]: string } = {
                'Authorization': res.identifer
              };
              // Attach the session identifier as a token header for logged-in users on qualifying endpoints
              const sessionIdentifier = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.Identifier);
              if (this.isSessionTokenEndpoint(req) && !!sessionIdentifier) {
                setHeaders['token'] = sessionIdentifier;
              }

              // Clone the request and add the DPoP token header
              const clone = req.clone({ setHeaders });
              return next.handle(clone);
            }
          }), catchError((err) => {
            if (err?.error?.statusCode === 401) {
              this.sessionService.clearData();
              // Removed session specific data
              this.sessionService.clearSessionStorage();
            }
            return throwError(err);
          }));
        })
      );
    }
  }
}
